// Organization Roles and Member Status
export enum OrganizationRole {
  OWNER = 'owner',
  ADMIN = 'admin',
  MEMBER = 'member',
  VIEWER = 'viewer',
}

export enum MemberStatus {
  ACTIVE = 'active',
  INVITED = 'invited',
  SUSPENDED = 'suspended',
}

// Session-specific User Permissions (extends existing SessionPermissions)
export enum SessionUserPermission {
  OWNER = 'owner',
  EDIT = 'edit',
  VIEW = 'view',
}

// Access Request Status
export enum AccessRequestStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  DENIED = 'denied',
  CANCELLED = 'cancelled',
}

// Domain-based Access Types
export enum DomainAccessType {
  AUTO_GRANT = 'auto_grant',
  MANUAL_APPROVAL = 'manual_approval',
  DISABLED = 'disabled',
}

export interface DomainAccessRule {
  domain: string;
  accessType: DomainAccessType;
  defaultPermission: SessionUserPermission;
  enabled: boolean;
  createdAt: Date;
  createdBy: {
    userId: string;
    email: string | null;
  };
}

// Organization Types
export interface FirestoreOrganization {
  name: string;
  createdAt: Date;
  createdBy: {
    userId: string;
    email: string | null;
  };
  ownerId: string;
  deletedAt?: Date; // Soft delete timestamp - if set, organization is deleted
  settings: {
    allowPublicSharing: boolean;
    defaultSessionPermissions: SessionUserPermission;
    domainBasedAccess: {
      enabled: boolean;
      maxOrgSize: number; // Performance limit for domain matching
      defaultPermission: SessionUserPermission;
    };
  };
}

export interface FirestoreOrganizationMember {
  userId: string;
  email: string;
  name: string; // Optional for invited users
  role: OrganizationRole;
  joinedAt: Date;
  invitedBy: {
    userId: string;
    email: string | null;
  };
  status: MemberStatus;
  invitationMessage?: string; // NEW: Optional invitation message
}

// Team Types
export interface FirestoreTeam {
  name: string;
  createdAt: Date;
  createdBy: {
    userId: string;
    email: string | null;
  };
  organizationId: string;
}

export interface FirestoreTeamMember {
  userId: string;
  email: string;
  name: string;
  joinedAt: Date;
  addedBy: {
    userId: string;
    email: string | null;
  };
  // Note: Only existing organization members can be added to teams
}

// Session Permission Types
export interface FirestoreSessionPermission {
  userId: string;
  permission: SessionUserPermission;
  grantedAt: Date;
  grantedBy: {
    userId: string;
    email: string | null;
  };
  status: boolean; // NEW: true = active permission, false = revoked
  grantedVia?:
    | 'manual'
    | 'domain_auto'
    | 'organization_join'
    | 'session_share'
    | 'team'
    | 'organization'
    | 'direct'; // How permission was granted
}

// Access Request Types
export interface FirestoreAccessRequest {
  userId: string;
  email: string;
  name: string;
  sessionId: string;
  sessionName: string;
  requestedPermission: SessionUserPermission;
  status: AccessRequestStatus;
  requestedAt: Date;
  message?: string; // Optional message from requester
  // Response fields (set when approved/denied)
  respondedAt?: Date;
  respondedBy?: {
    userId: string;
    email: string | null;
  };
  responseMessage?: string;
}

// Duplication Request Status (reuse AccessRequestStatus)
export type DuplicationRequestStatus = AccessRequestStatus;

// Duplication Request Types
export interface FirestoreDuplicationRequest {
  userId: string;
  email: string;
  name: string;
  sessionId: string;
  sessionName: string;
  sessionOwnerId: string; // NEW: Store session owner ID for efficient querying
  newSessionName: string;
  duplicationOptions: {
    sources: boolean;
    sourceRows: boolean;
    modules: boolean;
    filenameFormula: boolean;
    queueCards: boolean;
    resetCardStatus: boolean;
  };
  status: DuplicationRequestStatus;
  requestedAt: Date;
  message?: string; // Optional message from requester
  // Response fields (set when approved/denied)
  respondedAt?: Date;
  respondedBy?: {
    userId: string;
    email: string | null;
  };
  responseMessage?: string;
  // Result fields (set when duplication is completed)
  newSessionId?: string;
  duplicatedAt?: Date;
}

// Bug Report Status - admins can update to track progress
export type BugReportStatus =
  | 'pending' // New, not yet looked at
  | 'in_progress' // Someone is working on it
  | 'completed' // Fixed/resolved
  | 'wont_fix' // Decided not to fix
  | 'reviewed'; // Legacy - maps to completed in UI

export const BUG_REPORT_STATUS_OPTIONS: { value: BugReportStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'wont_fix', label: "Won't Fix" },
  { value: 'reviewed', label: 'Reviewed (legacy)' },
];

// Bug Report source - user-submitted vs auto-generated (e.g. permission checks)
export type BugReportType = 'user' | 'automated';

/** Converts Firestore Timestamp, Date, or number to Date for display. */
export function toDate(v: unknown): Date {
  if (v instanceof Date) return v;
  if (v && typeof (v as { toDate?: () => Date }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate();
  }
  return new Date(v as string | number);
}

export const reportTypeLabel = (t: BugReportType | undefined): string =>
  t === 'automated' ? 'Automated' : 'User';

// Bug Report Types (for Report Bug feature)
export interface FirestoreBugReport {
  userId: string;
  userEmail: string;
  userName: string;
  description: string;
  createdAt: Date;
  status: BugReportStatus;
  reportType?: BugReportType; // Default 'user' for existing reports
  jamDevUrl?: string; // Admin-pasted link to jam.dev recording
  linearTicketId?: string; // Admin-pasted Linear ticket ID (e.g. CRE-123)
}

export interface BugReportWithId extends FirestoreBugReport {
  id: string;
}
